import { useEffect, useRef, useState } from "react"
import Icon from "../../components/Icon"
import ConfirmModal from "../../components/ConfirmModal"
import { getApiErrorMessage } from "../../utils/errorMessage"
import { buildRequestFingerprint, clearStableRequestKey, getStableRequestKey } from "../../utils/requestKey"
import AutopayCompanyRow from "./AutopayCompanyRow"
import {
  buildPayBillConfirm,
  buildTurnOnConfirm,
  countWaitingCompanies,
  describeApprovalResult,
  describePaymentResult,
} from "./autopayApprovalDisplay"

const PAY_STORAGE_PREFIX = "utbills:pay-bill:"

// Every company that bills this person, each with an auto-pay switch. A company
// is only paid from the wallet by itself once the person turns it on; until then
// its bills wait here with a Pay button for paying one bill on purpose.
function AutopayCompaniesPanel({ companies = [], walletBalance = 0, focus = false, onSetApproval, onPayBill }) {
  const waitingCount = countWaitingCompanies(companies)
  const [open, setOpen] = useState(waitingCount > 0 || focus)
  const [confirm, setConfirm] = useState(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)
  const sectionRef = useRef(null)

  // Opened from a dashboard notification: show the panel and bring it into view.
  useEffect(() => {
    if (!focus) return
    setOpen(true)
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" })
  }, [focus])

  if (!companies.length) return null

  const saveApproval = async (company, approved) => {
    setBusy(true)
    setNotice(null)
    try {
      const result = await onSetApproval(company.providerId, approved)
      setNotice({ tone: "success", text: describeApprovalResult({ company, approved, payment: result?.payment }) })
      setConfirm(null)
    } catch (err) {
      setNotice({ tone: "error", text: getApiErrorMessage(err, "Could not save your choice. Please try again.") })
      setConfirm(null)
    } finally {
      setBusy(false)
    }
  }

  const payBill = async ({ company, bill, payNow }) => {
    const storageKey = `${PAY_STORAGE_PREFIX}${bill.invoiceId}`
    const requestKey = getStableRequestKey({
      storageKey,
      fingerprint: buildRequestFingerprint({ invoiceId: bill.invoiceId, payNow }),
      prefix: "utbill-pay",
    })
    setBusy(true)
    setNotice(null)
    try {
      const result = await onPayBill(bill.invoiceId, requestKey)
      clearStableRequestKey({ storageKey, requestKey })
      setNotice({ tone: "success", text: describePaymentResult({ company, result }) })
    } catch (err) {
      setNotice({ tone: "error", text: getApiErrorMessage(err, "Could not pay this bill. Please try again.") })
    } finally {
      setConfirm(null)
      setBusy(false)
    }
  }

  const handleToggle = (company, approved) => {
    // Turning a company off moves no money, so it saves at once. Turning it on
    // can pay bills, so the person sees exactly what will happen first.
    if (!approved) {
      saveApproval(company, false)
      return
    }
    setConfirm({ kind: "on", company, content: buildTurnOnConfirm(company, walletBalance) })
  }

  const handlePay = (company, bill) => {
    setConfirm({ kind: "pay", company, bill, content: buildPayBillConfirm(bill, company, walletBalance) })
  }

  const onConfirm = () => {
    if (!confirm) return
    if (confirm.kind === "on") saveApproval(confirm.company, true)
    else payBill({ company: confirm.company, bill: confirm.bill, payNow: confirm.content.payNow })
  }

  return (
    <section
      ref={sectionRef}
      className="scroll-mt-24 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      aria-label="Companies that bill you"
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex min-w-0 items-center gap-2">
          <Icon name="shield-check" size="1rem" className="shrink-0 text-teal-600" />
          <span className="truncate text-sm font-black text-slate-900 sm:text-base">Companies that bill you</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          {waitingCount > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
              {waitingCount} waiting
            </span>
          )}
          <Icon name={open ? "chevron-up" : "chevron-down"} size="0.9rem" className="text-slate-400" />
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100">
          <p className="px-4 pt-3 text-xs text-slate-500">
            Your wallet pays automatically only when Auto-Pay is ON. Enable it only for providers you know.
          </p>

          {notice && (
            <p
              role={notice.tone === "error" ? "alert" : "status"}
              className={`mx-4 mt-2 rounded-xl px-3 py-2 text-xs font-medium ${
                notice.tone === "error"
                  ? "border border-red-200 bg-red-50 text-red-700"
                  : "border border-emerald-200 bg-emerald-50 text-emerald-800"
              }`}
            >
              {notice.text}
            </p>
          )}

          <ul className="mt-2 divide-y divide-slate-100 border-t border-slate-100">
            {companies.map((company) => (
              <AutopayCompanyRow
                key={company.providerId}
                company={company}
                walletBalance={walletBalance}
                busy={busy}
                onToggle={handleToggle}
                onPay={(bill) => handlePay(company, bill)}
              />
            ))}
          </ul>
        </div>
      )}

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.content.title}
        message={confirm?.content.message}
        details={confirm?.content.details || []}
        icon={confirm?.kind === "pay" ? "wallet2" : "shield-exclamation"}
        tone={confirm?.kind === "pay" ? "teal" : "amber"}
        confirmLabel={confirm?.content.confirmLabel}
        busy={busy}
        onConfirm={onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </section>
  )
}

export default AutopayCompaniesPanel
